<template>
  <div class="relative mx-auto max-w-3xl overflow-hidden rounded-[28px] bg-white shadow-lg print:max-w-none print:rounded-none print:shadow-none">
    <!-- Top bar -->
    <div class="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div>

    <div class="p-6 sm:p-10">
      <!-- Scan to view (top right on larger screens / print, stacked above on mobile) -->
      <div v-if="viewUrl" class="mb-6 text-center sm:absolute sm:right-8 sm:top-8 sm:mb-0 print:absolute print:right-8 print:top-8">
        <QrImage :value="viewUrl" :size="88" class="mx-auto h-16 w-16" alt="Scan to view this invoice online" />
        <p class="mt-1 text-[8px] font-semibold uppercase tracking-wide text-gray-400">Scan to View</p>
      </div>

      <!-- Letterhead -->
      <div class="flex flex-col items-start gap-4 border-b border-amber-100 pb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-0 sm:pr-24 print:flex-row print:pr-24">
        <img :src="etimadLogo" alt="Al Etimad Haramain Travel" class="h-12 w-auto sm:h-14" />
        <div class="text-left text-xs leading-relaxed text-gray-500 sm:text-right print:text-right">
          <p>aletimadtravel@gmail.com</p>
          <p>+92 318 5073318</p>
          <p>aletimadtravel.com</p>
        </div>
      </div>

      <!-- Title -->
      <div class="mt-7 flex items-end justify-between">
        <div>
          <div class="flex items-center gap-2">
            <p class="text-xs font-semibold uppercase tracking-[0.4em] text-amber-500">Invoice</p>
            <span class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Billed in {{ invoice.currency || 'PKR' }}
            </span>
          </div>
          <h1 class="mt-1.5 text-2xl font-bold tracking-tight text-gray-900">{{ invoice.invoiceNo }}</h1>
        </div>
        <div class="text-right text-xs text-gray-500">
          <p class="uppercase tracking-wide">Invoice Date</p>
          <p class="mt-0.5 font-semibold text-gray-800">{{ invoice.invoiceDate }}</p>
        </div>
      </div>

      <!-- Hotel -->
      <div v-if="invoice.hotelName" class="mt-7 rounded-2xl border border-gray-100 bg-white p-5">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-amber-600">Hotel</p>
        <h2 class="mt-1 text-xl font-bold text-gray-900">{{ invoice.hotelName }}</h2>
        <div class="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <div v-if="invoice.view">
            <p class="text-[11px] uppercase tracking-wide text-gray-400">View</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ invoice.view }}</p>
          </div>
          <div v-if="invoice.hcn">
            <p class="text-[11px] uppercase tracking-wide text-gray-400">HCN</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ invoice.hcn }}</p>
          </div>
          <div v-if="invoice.paxCount">
            <p class="text-[11px] uppercase tracking-wide text-gray-400">Passengers</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ invoice.paxCount }}</p>
          </div>
          <div v-if="invoice.checkInDate">
            <p class="text-[11px] uppercase tracking-wide text-gray-400">Check-In</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ formatDate(invoice.checkInDate) }}</p>
          </div>
          <div v-if="invoice.checkOutDate">
            <p class="text-[11px] uppercase tracking-wide text-gray-400">Check-Out</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ formatDate(invoice.checkOutDate) }}</p>
          </div>
          <div v-if="nights">
            <p class="text-[11px] uppercase tracking-wide text-gray-400">Nights</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ nights }}</p>
          </div>
        </div>
      </div>

      <!-- Bill To -->
      <div class="mt-7 rounded-2xl bg-gray-50/80 p-5 text-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Bill To</p>
        <p class="mt-1 font-semibold text-gray-900">{{ invoice.customerName }}</p>
        <p v-if="invoice.customerAddress" class="whitespace-pre-line text-gray-600">{{ invoice.customerAddress }}</p>
        <p v-if="invoice.customerPhone" class="text-gray-600">{{ invoice.customerPhone }}</p>
        <p v-if="invoice.customerEmail" class="text-gray-600">{{ invoice.customerEmail }}</p>
      </div>

      <!-- Line items -->
      <div class="mt-7 overflow-x-auto rounded-2xl border border-gray-100">
        <table class="w-full min-w-[480px] text-sm">
          <thead>
            <tr class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th class="px-4 py-2 font-semibold">Description</th>
              <th class="px-4 py-2 font-semibold text-right">Qty</th>
              <th class="px-4 py-2 font-semibold text-right">Unit Price</th>
              <th class="px-4 py-2 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(row, i) in invoice.lineItems || []" :key="i">
              <td class="px-4 py-2.5 text-gray-700">{{ row.description }}</td>
              <td class="px-4 py-2.5 text-right text-gray-700">{{ row.quantity }}</td>
              <td class="px-4 py-2.5 text-right text-gray-700">{{ money(row.unitPrice) }}</td>
              <td class="px-4 py-2.5 text-right font-semibold text-gray-900">{{ money(row.quantity * row.unitPrice) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Totals -->
      <div class="mt-7 flex justify-end">
        <div class="w-64 space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-500">Subtotal</span>
            <span class="font-medium text-gray-900">{{ money(subtotal) }}</span>
          </div>
          <div v-if="invoice.discount" class="flex justify-between">
            <span class="text-gray-500">Discount</span>
            <span class="font-medium text-gray-900">- {{ money(invoice.discount) }}</span>
          </div>
          <div class="flex justify-between border-t border-gray-200 pt-2 text-base">
            <span class="font-semibold text-gray-900">Grand Total</span>
            <span class="font-bold text-amber-600">{{ money(grandTotal) }}</span>
          </div>
        </div>
      </div>

      <!-- Payment -->
      <div class="mt-7 flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-5 text-sm">
        <div>
          <p class="text-gray-500">Payment Method</p>
          <p class="font-semibold text-gray-900">{{ invoice.paymentMethod || '—' }}</p>
        </div>
        <span class="rounded-full bg-amber-500 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          {{ invoice.paymentStatus || 'Unpaid' }}
        </span>
      </div>

      <!-- Something Extra -->
      <div v-if="invoice.hasExtra && (invoice.extraTitle || invoice.extraDescription)" class="mt-7 rounded-2xl bg-gray-50/80 p-5">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Extra</p>
        <p v-if="invoice.extraTitle" class="mt-1 font-semibold text-gray-900">{{ invoice.extraTitle }}</p>
        <p v-if="invoice.extraDescription" class="mt-1 whitespace-pre-line text-sm text-gray-600">{{ invoice.extraDescription }}</p>
      </div>

      <p v-if="invoice.notes" class="mt-7 whitespace-pre-line text-sm text-gray-600">{{ invoice.notes }}</p>

      <PrintBankDetails />

      <div class="mt-10 border-t border-gray-100 pt-6 text-center text-xs text-gray-400">
        Thank you for your business with Al Etimad Haramain Travel.
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div>
  </div>
</template>

<script>
import QrImage from './QrImage.vue'
import PrintBankDetails from './PrintBankDetails.vue'
import { formatAmount } from '../services/currencyStore'
import etimadLogo from '../assets/images/etimad-logo-wide.png'

export default {
  name: 'InvoiceDocument',
  components: { QrImage, PrintBankDetails },
  props: {
    invoice: { type: Object, required: true },
    viewUrl: { type: String, default: '' }
  },
  data() {
    return { etimadLogo }
  },
  computed: {
    subtotal() {
      return (this.invoice.lineItems || []).reduce(
        (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0),
        0
      )
    },
    grandTotal() {
      return Math.max(this.subtotal - (Number(this.invoice.discount) || 0), 0)
    },
    nights() {
      if (!this.invoice.checkInDate || !this.invoice.checkOutDate) return null
      const inD = new Date(`${this.invoice.checkInDate}T00:00:00`)
      const outD = new Date(`${this.invoice.checkOutDate}T00:00:00`)
      const diff = Math.round((outD - inD) / 86400000)
      return diff > 0 ? diff : null
    }
  },
  methods: {
    money(amount) {
      return formatAmount(amount, this.invoice.currency || 'PKR')
    },
    formatDate(isoDate) {
      if (!isoDate) return ''
      const date = new Date(`${isoDate}T00:00:00`)
      if (Number.isNaN(date.getTime())) return isoDate
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }
  }
}
</script>
